import { ENEMY_PROFILES, UPGRADES, WAVE_SCHEDULES } from "../content/game-content";
import {
  CENTER,
  add,
  clamp,
  clampMagnitude,
  direction,
  distanceSquared,
  dot,
  length,
  moveAngle,
  normalize,
  scale,
  shortestAngle,
  subtract
} from "./math";
import { nextRandom, randomRange } from "./rng";
import type {
  Enemy,
  EnemyKind,
  GameCommand,
  GameState,
  Projectile,
  SpawnItem,
  Tower,
  Upgrade,
  UpgradeId,
  Vec2
} from "./types";

const GUST_COST = 42;
const GUST_COOLDOWN = 0.55;
const HEARTH_RADIUS = 72;
const SPAWN_RADIUS = 540;
const AIM_DISTANCE = 560;
const AIM_CONE = 0.68;
const PROJECTILE_SPEED = 228;
const PROJECTILE_MIN_SPEED = 188;
const PROJECTILE_MAX_SPEED = 262;
const PROJECTILE_TURN_RATE = 1.08;
const PROJECTILE_RANGE = 1450;
const WIND_FORCE = 118;
const INITIAL_WIND = Math.PI;

const DEFAULT_MODIFIERS = {
  projectileDamage: 10,
  projectileCount: 1,
  projectileWindResponse: 1,
  curvedDamageMultiplier: 1.5,
  projectilePierce: 0,
  towerFireRate: 1,
  pressureCapacity: 100,
  pressureGeneration: 8.5,
  gustCost: GUST_COST,
  gustReloadsTowers: false,
  gustRadius: 420,
  hearthMax: 100
};

export function createGame(seed = 0x4b495445): GameState {
  const schedule = copySchedule(0);
  return {
    phase: "running",
    seed,
    rngState: seed || 1,
    time: 0,
    wave: 0,
    waveTime: 0,
    nextSpawn: 0,
    nextId: 1,
    score: 0,
    chain: 0,
    chainTime: 0,
    wind: {
      angle: INITIAL_WIND,
      targetAngle: INITIAL_WIND,
      strength: 1
    },
    aim: {
      point: add(CENTER, scale(direction(0), AIM_DISTANCE)),
      targetId: null
    },
    pressure: 58,
    gustCooldown: 0,
    hearth: {
      health: DEFAULT_MODIFIERS.hearthMax,
      maxHealth: DEFAULT_MODIFIERS.hearthMax,
      flash: 0
    },
    towers: [0, Math.PI * (2 / 3), Math.PI * (4 / 3)].map((angle, index) => ({
      id: index + 1,
      angle,
      radius: 132,
      cooldown: 0,
      fireRate: 1.38,
      targetId: null
    })),
    enemies: [],
    projectiles: [],
    schedule,
    modifiers: { ...DEFAULT_MODIFIERS },
    upgrades: [],
    offeredUpgrades: [],
    tutorial: {
      windMoved: false,
      firstHit: false
    },
    events: [{ kind: "wave-start" }],
    statistics: {
      shots: 0,
      hits: 0,
      defeats: 0,
      gusts: 0,
      damageTaken: 0,
      misses: 0,
      breaches: 0,
      curvedHits: 0
    }
  };
}

export function updateGame(state: GameState, command: GameCommand, dt: number): void {
  state.events = [];
  if (state.phase !== "running") {
    return;
  }

  const step = clamp(dt, 0, 1 / 20);
  state.time += step;
  state.gustCooldown = Math.max(0, state.gustCooldown - step);
  state.hearth.flash = Math.max(0, state.hearth.flash - step * 3);
  state.chainTime = Math.max(0, state.chainTime - step);
  if (state.chainTime === 0) {
    state.chain = 0;
  }

  updateWind(state, command, step);
  updatePressure(state, step);

  state.waveTime += step;

  if (command.gust && state.gustCooldown <= 0 && state.pressure >= state.modifiers.gustCost) {
    releaseGust(state);
  }

  spawnReadyEnemies(state);
  updateTowerCooldowns(state, step);
  handleFireCommand(state, command);
  updateAimTarget(state);
  updateProjectiles(state, step);
  resolveProjectileHits(state);
  updateEnemies(state, step);
  resolveHearthHits(state);
  removeExpiredEntities(state);
  resolveWaveState(state);
}

export function chooseUpgrade(state: GameState, id: UpgradeId): void {
  if (state.phase !== "upgrade" || !state.offeredUpgrades.includes(id)) {
    return;
  }

  state.upgrades.push(id);
  applyUpgrade(state, id);
  state.wave += 1;
  state.waveTime = 0;
  state.nextSpawn = 0;
  state.schedule = copySchedule(state.wave);
  state.offeredUpgrades = [];
  state.phase = "running";
  state.events = [{ kind: "wave-start" }];
}

export function getOfferedUpgrades(state: GameState): Upgrade[] {
  return state.offeredUpgrades.map((id) => UPGRADES[id]);
}

export function canGust(state: GameState): boolean {
  return state.phase === "running" && state.pressure >= state.modifiers.gustCost && state.gustCooldown <= 0;
}

export function getGustCost(state?: GameState): number {
  return state?.modifiers.gustCost ?? GUST_COST;
}

export function getRemainingThreats(state: GameState): number {
  return Math.max(0, state.schedule.length - state.nextSpawn + state.enemies.length);
}

export function getIncomingThreats(state: GameState): number {
  return Math.max(0, state.schedule.length - state.nextSpawn);
}

export function getEstimatedImpactTime(enemy: Enemy): number {
  const profile = ENEMY_PROFILES[enemy.kind];
  const distance = Math.max(0, length(subtract(enemy.position, CENTER)) - HEARTH_RADIUS - enemy.radius);
  const inward = normalize(subtract(CENTER, enemy.position));
  const inwardSpeed = Math.max(profile.thrust * 0.65, dot(enemy.velocity, inward));
  return distance / inwardSpeed;
}

export function getProjectileForecast(state: GameState, tower: Tower, steps = 60): Vec2[] {
  const origin = getTowerPosition(tower);
  let angle = getTowerLaunchAngle(state, tower);
  let speed = PROJECTILE_SPEED;
  let position = { ...origin };
  const wind = direction(state.wind.angle);
  const points: Vec2[] = [{ ...position }];
  for (let index = 0; index < steps; index += 1) {
    const forward = direction(angle);
    const crosswind = forward.x * wind.y - forward.y * wind.x;
    const tailwind = dot(forward, wind);
    angle += crosswind * PROJECTILE_TURN_RATE * state.modifiers.projectileWindResponse / 30;
    speed = clamp(
      speed + tailwind * 18 * state.modifiers.projectileWindResponse / 30,
      PROJECTILE_MIN_SPEED,
      PROJECTILE_MAX_SPEED
    );
    position = add(position, scale(direction(angle), speed / 30));
    if (index % 4 === 3) {
      points.push({ ...position });
    }
  }
  return points;
}

function copySchedule(wave: number): SpawnItem[] {
  const source = WAVE_SCHEDULES[wave] ?? [];
  return source.map((item) => ({ ...item }));
}

function updateWind(state: GameState, command: GameCommand, dt: number): void {
  const firstAim = !state.tutorial.windMoved && typeof command.aimAngle === "number";
  const steering = typeof command.aimAngle === "number" || Boolean(command.rotation);
  if (steering) {
    for (const projectile of state.projectiles) {
      projectile.steeringActive = true;
    }
  }
  if (typeof command.aimAngle === "number") {
    state.wind.targetAngle = command.aimAngle;
    if (!state.tutorial.windMoved) {
      state.tutorial.windMoved = true;
      state.events.push({ kind: "wind-set" });
    }
  }
  if (command.rotation) {
    state.wind.targetAngle += command.rotation * dt * 2.45;
  }
  state.wind.angle = firstAim
    ? state.wind.targetAngle
    : moveAngle(state.wind.angle, state.wind.targetAngle, dt * 3.4);
}

function updatePressure(state: GameState, dt: number): void {
  const turbineAlignment = (
    Math.abs(Math.sin(state.wind.angle)) +
    Math.abs(Math.sin(state.wind.angle - Math.PI * (2 / 3))) +
    Math.abs(Math.sin(state.wind.angle - Math.PI * (4 / 3)))
  ) / 3;
  const generation = state.modifiers.pressureGeneration * (0.6 + turbineAlignment * 0.7);
  state.pressure = Math.min(state.modifiers.pressureCapacity, state.pressure + generation * dt);
}

function releaseGust(state: GameState): void {
  const wind = direction(state.wind.angle);
  state.pressure -= state.modifiers.gustCost;
  state.gustCooldown = GUST_COOLDOWN;
  state.statistics.gusts += 1;
  state.events.push({ kind: "gust", x: CENTER.x, y: CENTER.y });
  if (state.modifiers.gustReloadsTowers) {
    for (const tower of state.towers) {
      tower.cooldown = 0;
    }
  }

  for (const enemy of state.enemies) {
    const offset = subtract(enemy.position, CENTER);
    const distance = length(offset);
    const forward = dot(normalize(offset), wind);
    if (distance < state.modifiers.gustRadius && forward > -0.08) {
      const profile = ENEMY_PROFILES[enemy.kind];
      const falloff = 1 - distance / state.modifiers.gustRadius;
      const impulse = 285 * (0.35 + falloff * 0.65) * profile.windResponse / profile.mass;
      enemy.velocity = add(enemy.velocity, scale(wind, impulse));
    }
  }

  for (const projectile of state.projectiles) {
    const distance = length(subtract(projectile.position, CENTER));
    if (distance < state.modifiers.gustRadius * 1.2) {
      const currentAngle = Math.atan2(projectile.velocity.y, projectile.velocity.x);
      const turn = clamp(shortestAngle(currentAngle, state.wind.angle), -0.72, 0.72) * projectile.windResponse;
      const nextAngle = currentAngle + turn;
      projectile.turnAmount += Math.abs(turn);
      projectile.speed = Math.min(PROJECTILE_MAX_SPEED, Math.max(PROJECTILE_MIN_SPEED, projectile.speed + 28));
      projectile.velocity = scale(direction(nextAngle), projectile.speed);
    }
  }
}

function spawnReadyEnemies(state: GameState): void {
  let item = state.schedule[state.nextSpawn];
  while (item && item.at <= state.waveTime) {
    spawnEnemy(state, item.kind, item.angle);
    state.nextSpawn += 1;
    item = state.schedule[state.nextSpawn];
  }
}

function spawnEnemy(state: GameState, kind: EnemyKind, angle: number): void {
  const profile = ENEMY_PROFILES[kind];
  let variation: number;
  [state.rngState, variation] = randomRange(state.rngState, -0.06, 0.06);
  const spawnAngle = angle + variation;
  const position = add(CENTER, scale(direction(spawnAngle), SPAWN_RADIUS));
  const inward = normalize(subtract(CENTER, position));

  state.enemies.push({
    id: state.nextId,
    kind,
    position,
    velocity: scale(inward, profile.thrust * 0.4),
    health: profile.health,
    maxHealth: profile.health,
    radius: profile.radius,
    phase: variation * 30 + state.nextId * 0.73,
    flash: 0
  });
  state.nextId += 1;
}

function updateTowerCooldowns(state: GameState, dt: number): void {
  for (const tower of state.towers) {
    tower.cooldown = Math.max(0, tower.cooldown - dt);
  }
}

function handleFireCommand(state: GameState, command: GameCommand): void {
  if (!command.fireAt && !command.fire) {
    return;
  }
  const requestedPoint = command.fireAt ?? state.aim.point;
  const target = command.fireAt
    ? nearestEnemyAtPoint(state.enemies, requestedPoint)
    : mostAlignedEnemy(state.enemies, state.wind.targetAngle);
  state.aim = {
    point: target ? { ...target.position } : { ...requestedPoint },
    targetId: target?.id ?? null
  };
  for (const tower of state.towers) {
    tower.targetId = state.aim.targetId;
    if (tower.cooldown > 0) {
      continue;
    }
    const origin = getTowerPosition(tower);
    const heading = getTowerLaunchAngle(state, tower);
    for (let index = 0; index < state.modifiers.projectileCount; index += 1) {
      const lateral = (index - (state.modifiers.projectileCount - 1) / 2) * 13;
      const offset = scale(direction(heading + Math.PI / 2), lateral);
      launchProjectile(state, add(origin, offset), heading);
    }
    tower.cooldown = tower.fireRate / state.modifiers.towerFireRate;
  }
}

function nearestEnemyAtPoint(enemies: Enemy[], point: Vec2): Enemy | undefined {
  let selected: Enemy | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const enemy of enemies) {
    const distance = Math.sqrt(distanceSquared(enemy.position, point));
    const selectionRadius = enemy.radius + 52;
    if (distance <= selectionRadius && distance < bestDistance) {
      selected = enemy;
      bestDistance = distance;
    }
  }
  return selected;
}

function updateAimTarget(state: GameState): void {
  if (state.aim.targetId === null) {
    return;
  }
  const target = state.enemies.find((enemy) => enemy.id === state.aim.targetId);
  if (target) {
    state.aim.point = { ...target.position };
    return;
  }
  state.aim.targetId = null;
  for (const tower of state.towers) {
    tower.targetId = null;
  }
}

function mostAlignedEnemy(enemies: Enemy[], aimAngle: number): Enemy | undefined {
  let selected: Enemy | undefined;
  let bestScore = Number.POSITIVE_INFINITY;
  let bestAngularError = Number.POSITIVE_INFINITY;
  for (const enemy of enemies) {
    const offset = subtract(enemy.position, CENTER);
    const enemyAngle = Math.atan2(offset.y, offset.x);
    const angularError = Math.abs(shortestAngle(aimAngle, enemyAngle));
    const urgency = Math.min(12, getEstimatedImpactTime(enemy));
    const candidate = angularError * 10 + urgency * 0.035;
    if (candidate < bestScore) {
      selected = enemy;
      bestScore = candidate;
      bestAngularError = angularError;
    }
  }
  return bestAngularError <= AIM_CONE ? selected : undefined;
}

export function getTowerPosition(tower: Tower): Vec2 {
  return add(CENTER, scale(direction(tower.angle), tower.radius));
}

export function getTowerLaunchAngle(state: GameState, tower: Tower): number {
  const origin = getTowerPosition(tower);
  const selectedTarget = state.enemies.find((enemy) => enemy.id === tower.targetId);
  const aimPoint = selectedTarget?.position ?? state.aim.point;
  return Math.atan2(aimPoint.y - origin.y, aimPoint.x - origin.x);
}

function launchProjectile(state: GameState, origin: Vec2, heading: number): void {
  const projectile: Projectile = {
    id: state.nextId,
    position: { ...origin },
    previous: { ...origin },
    velocity: scale(direction(heading), PROJECTILE_SPEED),
    radius: 8,
    damage: state.modifiers.projectileDamage,
    curvedDamageMultiplier: state.modifiers.curvedDamageMultiplier,
    steeringActive: false,
    windResponse: state.modifiers.projectileWindResponse,
    lifetime: 8,
    pierce: state.modifiers.projectilePierce,
    speed: PROJECTILE_SPEED,
    targetId: state.aim.targetId,
    distanceTravelled: 0,
    turnAmount: 0,
    trail: [{ ...origin }],
    consumed: false
  };
  state.nextId += 1;
  state.projectiles.push(projectile);
  state.statistics.shots += 1;
  state.events.push({ kind: "launch", x: origin.x, y: origin.y });
}

function updateProjectiles(state: GameState, dt: number): void {
  const wind = direction(state.wind.angle);
  for (const projectile of state.projectiles) {
    projectile.previous = { ...projectile.position };
    if (!projectile.steeringActive) {
      projectile.velocity = scale(normalize(projectile.velocity), projectile.speed);
      projectile.position = add(projectile.position, scale(projectile.velocity, dt));
      projectile.distanceTravelled += projectile.speed * dt;
      projectile.lifetime -= dt;
      projectile.trail.push({ ...projectile.position });
      if (projectile.trail.length > 14) {
        projectile.trail.shift();
      }
      continue;
    }
    const forward = normalize(projectile.velocity);
    const crosswind = forward.x * wind.y - forward.y * wind.x;
    const tailwind = dot(forward, wind);
    const turn = crosswind * PROJECTILE_TURN_RATE * projectile.windResponse * dt;
    const angle = Math.atan2(forward.y, forward.x) + turn;
    projectile.speed = clamp(
      projectile.speed + tailwind * 18 * projectile.windResponse * dt,
      PROJECTILE_MIN_SPEED,
      PROJECTILE_MAX_SPEED
    );
    projectile.velocity = scale(direction(angle), projectile.speed);
    projectile.position = add(projectile.position, scale(projectile.velocity, dt));
    projectile.distanceTravelled += projectile.speed * dt;
    projectile.turnAmount += Math.abs(turn);
    projectile.lifetime -= dt;
    projectile.trail.push({ ...projectile.position });
    if (projectile.trail.length > 14) {
      projectile.trail.shift();
    }
  }
}

function resolveProjectileHits(state: GameState): void {
  const defeated = new Set<number>();
  for (const projectile of state.projectiles) {
    if (projectile.lifetime <= 0) {
      continue;
    }
    for (const enemy of state.enemies) {
      if (defeated.has(enemy.id)) {
        continue;
      }
      const collisionRadius = projectile.radius + enemy.radius;
      if (distanceSquared(projectile.position, enemy.position) > collisionRadius * collisionRadius) {
        continue;
      }
      const curved = projectile.turnAmount >= 0.42;
      const damage = projectile.damage * (curved ? projectile.curvedDamageMultiplier : 1);
      enemy.health -= damage;
      enemy.flash = 1;
      projectile.pierce -= 1;
      state.statistics.hits += 1;
      state.tutorial.firstHit = true;
      state.events.push({ kind: "hit", x: enemy.position.x, y: enemy.position.y, amount: damage, enemyKind: enemy.kind });
      if (curved) {
        state.statistics.curvedHits += 1;
        state.events.push({ kind: "curved-hit", x: enemy.position.x, y: enemy.position.y, enemyKind: enemy.kind });
      }
      if (enemy.health <= 0) {
        defeated.add(enemy.id);
        registerEnemyDefeat(state, enemy);
      }
      if (projectile.pierce < 0) {
        projectile.lifetime = 0;
        projectile.consumed = true;
        break;
      }
    }
  }
  if (defeated.size > 0) {
    state.enemies = state.enemies.filter((enemy) => !defeated.has(enemy.id));
  }
}

function registerEnemyDefeat(state: GameState, enemy: Enemy): void {
  const profile = ENEMY_PROFILES[enemy.kind];
  state.chain = state.chainTime > 0 ? state.chain + 1 : 1;
  state.chainTime = 1.1;
  state.score += profile.score * Math.min(5, state.chain);
  state.statistics.defeats += 1;
  state.events.push({ kind: "defeat-enemy", x: enemy.position.x, y: enemy.position.y, enemyKind: enemy.kind });
}

function updateEnemies(state: GameState, dt: number): void {
  const wind = direction(state.wind.angle);
  for (const enemy of state.enemies) {
    const profile = ENEMY_PROFILES[enemy.kind];
    const inward = normalize(subtract(CENTER, enemy.position));
    const sideways = { x: -inward.y, y: inward.x };
    const tack = Math.sin(state.time * 1.45 + enemy.phase) * profile.lateralResponse;
    const desired = add(scale(inward, profile.thrust), scale(sideways, profile.thrust * tack));
    const radialWind = dot(wind, inward);
    const lateralWind = subtract(wind, scale(inward, radialWind));
    const readableWind = add(lateralWind, scale(inward, radialWind * 0.12));
    const windAcceleration = scale(readableWind, WIND_FORCE * profile.windResponse / profile.mass);
    const steering = scale(subtract(desired, enemy.velocity), 0.72);
    enemy.velocity = add(enemy.velocity, scale(add(steering, windAcceleration), dt));
    enemy.velocity = clampMagnitude(enemy.velocity, kindMaximumSpeed(enemy.kind));
    enemy.position = add(enemy.position, scale(enemy.velocity, dt));
    const fromCenter = subtract(enemy.position, CENTER);
    const radialDistance = length(fromCenter);
    if (radialDistance > SPAWN_RADIUS + 70) {
      const outward = normalize(fromCenter);
      enemy.position = add(CENTER, scale(outward, SPAWN_RADIUS + 70));
      const outwardSpeed = dot(enemy.velocity, outward);
      if (outwardSpeed > 0) {
        enemy.velocity = subtract(enemy.velocity, scale(outward, outwardSpeed));
      }
      enemy.velocity = add(enemy.velocity, scale(inward, profile.thrust * 0.35));
    }
    enemy.flash = Math.max(0, enemy.flash - dt * 5);
  }
}

function kindMaximumSpeed(kind: EnemyKind): number {
  if (kind === "skiff") {
    return 128;
  }
  if (kind === "tacker" || kind === "cinder") {
    return 102;
  }
  if (kind === "tempest") {
    return 54;
  }
  return 62;
}

function resolveHearthHits(state: GameState): void {
  const survivors: Enemy[] = [];
  for (const enemy of state.enemies) {
    const threshold = HEARTH_RADIUS + enemy.radius * 0.45;
    if (distanceSquared(enemy.position, CENTER) > threshold * threshold) {
      survivors.push(enemy);
      continue;
    }
    const damage = ENEMY_PROFILES[enemy.kind].contactDamage;
    state.hearth.health = Math.max(0, state.hearth.health - damage);
    state.hearth.flash = 1;
    state.statistics.damageTaken += damage;
    state.statistics.breaches += 1;
    state.events.push({ kind: "hearth-hit", x: CENTER.x, y: CENTER.y, amount: damage, enemyKind: enemy.kind });
  }
  state.enemies = survivors;
  if (state.hearth.health <= 0) {
    state.phase = "defeat";
    state.events.push({ kind: "defeat" });
  }
}

function removeExpiredEntities(state: GameState): void {
  state.projectiles = state.projectiles.filter((projectile) => {
    if (projectile.consumed) {
      return false;
    }
    const offset = subtract(projectile.position, CENTER);
    const outsideStorm = Math.abs(offset.x) >= 650 || Math.abs(offset.y) >= 650;
    const exhausted = projectile.lifetime <= 0 || projectile.distanceTravelled >= PROJECTILE_RANGE;
    if (!outsideStorm && !exhausted) {
      return true;
    }
    state.statistics.misses += 1;
    state.events.push({ kind: "projectile-lost", x: projectile.position.x, y: projectile.position.y });
    return false;
  });
}

function resolveWaveState(state: GameState): void {
  if (state.phase !== "running" || state.nextSpawn < state.schedule.length || state.enemies.length > 0) {
    return;
  }
  if (state.wave >= WAVE_SCHEDULES.length - 1) {
    state.phase = "victory";
    state.score += Math.round(state.hearth.health * 20);
    state.events.push({ kind: "victory" });
    return;
  }
  state.phase = "upgrade";
  state.projectiles = [];
  state.events.push({ kind: "wave-clear" });
  state.offeredUpgrades = drawUpgradeOffer(state);
}

function drawUpgradeOffer(state: GameState): UpgradeId[] {
  const available = (Object.keys(UPGRADES) as UpgradeId[]).filter((id) => !state.upgrades.includes(id));
  const offered: UpgradeId[] = [];
  while (available.length > 0 && offered.length < 3) {
    let value: number;
    [state.rngState, value] = nextRandom(state.rngState);
    const index = Math.floor(value * available.length);
    const [selected] = available.splice(index, 1);
    if (selected) {
      offered.push(selected);
    }
  }
  return offered;
}

function applyUpgrade(state: GameState, id: UpgradeId): void {
  switch (id) {
    case "split-fletching":
      state.modifiers.projectileCount += 1;
      break;
    case "copper-vanes":
      state.modifiers.pressureCapacity += 20;
      state.modifiers.gustReloadsTowers = true;
      state.pressure += 20;
      break;
    case "shear-layer":
      state.modifiers.projectileWindResponse *= 1.8;
      state.modifiers.curvedDamageMultiplier = Math.max(state.modifiers.curvedDamageMultiplier, 2.5);
      break;
    case "deep-breath":
      state.modifiers.pressureGeneration *= 1.5;
      state.modifiers.gustCost *= 0.6;
      break;
    case "hearthglass":
      state.modifiers.hearthMax += 15;
      state.hearth.maxHealth += 15;
      state.hearth.health = Math.min(state.hearth.maxHealth, state.hearth.health + 25);
      break;
    case "needlecraft":
      state.modifiers.towerFireRate *= 1.35;
      state.modifiers.projectileDamage += 6;
      break;
  }
}
